import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import CustomDropdown from './ui/CustomDropdown';
import Header from './Header';
import FeatureUpsellPage from './ui/FeatureUpsellPage';
import {
  TrendingUp,
  Search,
  Key,
  Save,
  X,
  SlidersHorizontal,
  Youtube,
  Eye,
  ThumbsUp,
  MessageSquare,
  Calendar,
  Clock,
  ExternalLink,
  Filter,
  RotateCcw,
  Users,
  Bookmark,
  BookmarkPlus,
  Play,
  Trash2,
  BookOpen,
  ArrowRight,
  Gauge
} from 'lucide-react';
import PageHeader from './ui/PageHeader';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VideosVirais = ({ onLogout }) => {
  const { t } = useTranslation();
  usePageTitle(t('viralVideos.page_title'));
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [accessCheck, setAccessCheck] = useState({ loading: true, hasAccess: false, message: '' });
  // (Fase 4 da unificação: a checagem de API key do usuário foi removida —
  //  as buscas usam o hub global de chaves do sistema.)
  
  // Search filters
  const [searchTerm, setSearchTerm] = useState('');
  const [publishedAfter, setPublishedAfter] = useState('');
  const [publishedBefore, setPublishedBefore] = useState('');
  const [minViews, setMinViews] = useState('');
  const [publishedAgoFilter, setPublishedAgoFilter] = useState('all'); // Filtro "Publicado há"
  const [minDuration, setMinDuration] = useState('300'); // Filtro "Duração Mínima" (padrão 300 segundos = 5 minutos)
  
  // Sorting
  const [sortBy, setSortBy] = useState('views');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Insights hover state
  const [hoveredInsights, setHoveredInsights] = useState(null);
  
  // Saved searches
  const [savedSearches, setSavedSearches] = useState(() => {
    const saved = localStorage.getItem('saved_searches_viral_videos');
    return saved ? JSON.parse(saved) : [];
  });
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [searchName, setSearchName] = useState('');
  
  // Saved videos states
  const [activeTab, setActiveTab] = useState('search'); // 'search' or 'saved'
  const [savedVideos, setSavedVideos] = useState([]);
  const [savedVideosLoading, setSavedVideosLoading] = useState(false);
  const [savingVideo, setSavingVideo] = useState(null); // Track which video is being saved

  const { isDarkMode } = useTheme();
  const { sidebarWidth, isMobile } = useSidebar();

  // Tooltip for truncated titles
  const [tooltipText, setTooltipText] = useState('');
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  const handleTitleHover = useCallback((e, title) => {
    const el = e.currentTarget;
    if (el.scrollHeight > el.clientHeight) {
      const rect = el.getBoundingClientRect();
      setTooltipText(title);
      setTooltipPos({ x: rect.left + rect.width / 2, y: rect.top - 8 });
      setShowTooltip(true);
    }
  }, []);

  const handleTitleLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  // Reordenar vídeos quando o filtro de ordenação mudar
  const sortVideos = useCallback((videoList, sortField, order) => {
    return [...videoList].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      // Handle date sorting
      if (sortField === 'publishedAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      // Handle score sorting - calcular score para cada vídeo
      if (sortField === 'score') {
        aVal = calcularScore(a);
        bVal = calcularScore(b);
      }
      
      // Handle VPH sorting - calcular views por hora
      if (sortField === 'vph') {
        const calculateVPH = (video) => {
          const now = new Date();
          const publishedDate = new Date(video.publishedAt);
          const hoursAgo = (now - publishedDate) / (1000 * 60 * 60);
          return hoursAgo > 0 ? video.views / hoursAgo : 0;
        };
        aVal = calculateVPH(a);
        bVal = calculateVPH(b);
      }
      
      return order === 'desc' ? bVal - aVal : aVal - bVal;
    });
  }, []);

  useEffect(() => {
    if (videos.length > 0) {
      const sorted = sortVideos(videos, sortBy, sortOrder);
      const hasChanged = sorted.some((video, index) => video.id !== videos[index]?.id);
      if (hasChanged) {
        setVideos(sorted);
      }
    }
  }, [sortBy, sortOrder, sortVideos, videos]);

  const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    // BUG FIX: usa o SELO (videos_virais_acesso de /auth/subscription) — MESMA
    // fonte das outras 3 páginas e do require_premium do backend. Antes usava
    // /videos-virais/check-access (status 'ativo' SEM estado efetivo), que vazava
    // a tela p/ pro vencido (efetivo=free). Estado efetivo: free/basic/lite/
    // expirado → bloqueado; pro/plus → liberado.
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/auth/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccessCheck({
        loading: false,
        hasAccess: !!data.is_super_admin || !!data.videos_virais_acesso,
        message: '',
      });
    } catch (error) {
      console.error('Erro ao verificar acesso:', error);
      setAccessCheck({ loading: false, hasAccess: false, message: '' });
    }
  };


  const publishedAgoOptions = [
    { value: 'all', label: t('viralVideos.any_date') },
    { value: '1h', label: t('viralVideos.last_hour'), hours: 1 },
    { value: '3h', label: t('viralVideos.last_3_hours'), hours: 3 },
    { value: '6h', label: t('viralVideos.last_6_hours'), hours: 6 },
    { value: '12h', label: t('viralVideos.last_12_hours'), hours: 12 },
    { value: '24h', label: t('viralVideos.last_24_hours'), hours: 24 },
    { value: '3d', label: t('viralVideos.last_3_days'), hours: 72 },
    { value: '7d', label: t('viralVideos.last_week'), hours: 168 },
    { value: '14d', label: t('viralVideos.last_2_weeks'), hours: 336 },
    { value: '30d', label: t('viralVideos.last_month'), hours: 720 },
    { value: '90d', label: t('viralVideos.last_3_months'), hours: 2160 },
    { value: '180d', label: t('viralVideos.last_6_months'), hours: 4320 },
    { value: '365d', label: t('viralVideos.last_year'), hours: 8760 }
  ];

  // ===================================
  // SAVED VIDEOS FUNCTIONS
  // ===================================
  
  // Load saved videos when switching to saved tab
  useEffect(() => {
    if (activeTab === 'saved') {
      loadSavedVideos();
    }
  }, [activeTab]);

  const loadSavedVideos = async () => {
    setSavedVideosLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/videos-salvos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSavedVideos(response.data.videos);
      
      if (response.data.videos.length === 0) {
        toast.info(t('viralVideos.no_saved_videos_info'));
      }
    } catch (error) {
      console.error('Error loading saved videos:', error);
      toast.error(t('viralVideos.error_load_saved'));
    } finally {
      setSavedVideosLoading(false);
    }
  };

  const handleSaveVideo = async (video) => {
    // Check if video is already saved
    const isAlreadySaved = savedVideos.some(sv => sv.video_id === video.id);
    if (isAlreadySaved) {
      toast.info(t('viralVideos.video_already_saved'));
      return;
    }

    setSavingVideo(video.id);
    try {
      const token = localStorage.getItem('token');
      const videoData = {
        video_id: video.id,
        video_data: {
          thumbnail: video.thumbnail,
          title: video.title,
          views: video.views,
          likes: video.likes,
          comments: video.comments,
          channelTitle: video.channelTitle,
          channelId: video.channelId,
          channelSubscribers: video.channelSubscribers,
          duration: video.duration,
          publishedAt: video.publishedAt
        },
        termo_busca: searchTerm || t('viralVideos.search_no_term')
      };

      const response = await axios.post(`${API}/videos-salvos`, videoData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`✅ ${response.data.message} (${response.data.saved_count}/20)`);
      
      // Reload saved videos if we're on saved tab
      if (activeTab === 'saved') {
        loadSavedVideos();
      }
    } catch (error) {
      console.error('Error saving video:', error);
      const errorMsg = error.response?.data?.detail || t('viralVideos.error_save_video');
      toast.error(errorMsg);
    } finally {
      setSavingVideo(null);
    }
  };

  const handleDeleteSavedVideo = async (videoId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/videos-salvos/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`✅ ${response.data.message} (${response.data.saved_count}/20)`);
      
      // Remove from local state
      setSavedVideos(prev => prev.filter(v => v.video_id !== videoId));
    } catch (error) {
      console.error('Error deleting video:', error);
      const errorMsg = error.response?.data?.detail || t('viralVideos.error_remove_video');
      toast.error(errorMsg);
    }
  };

  // ===================================
  // END SAVED VIDEOS FUNCTIONS
  // ===================================

  // 📊 FUNÇÕES DE MÉTRICAS AVANÇADAS
  const calcularMetricas = (video) => {
    const { views, likes, comments, publishedAt } = video;
    
    // Dias desde publicação
    const diasDesdePublicacao = Math.max(1, Math.floor((new Date() - new Date(publishedAt)) / (1000 * 60 * 60 * 24)));
    
    // Ensure views is a valid number
    const validViews = Math.max(0, parseInt(views) || 0);
    const validLikes = Math.max(0, parseInt(likes) || 0);
    const validComments = Math.max(0, parseInt(comments) || 0);
    
    // Taxa de engajamento (avoid division by zero)
    const engajamento = validViews > 0 ? ((validLikes + validComments) / validViews) * 100 : 0;
    
    // Views por dia
    const viewsPorDia = validViews / diasDesdePublicacao;
    
    // Like ratio (avoid division by zero)
    const likeRatio = validViews > 0 ? (validLikes / validViews) * 100 : 0;
    
    return {
      engajamento: isNaN(engajamento) ? 0 : parseFloat(engajamento.toFixed(2)),
      viewsPorDia: isNaN(viewsPorDia) ? 0 : Math.round(viewsPorDia),
      likeRatio: isNaN(likeRatio) ? 0 : parseFloat(likeRatio.toFixed(2)),
      diasDesdePublicacao
    };
  };

  const calcularScore = (video) => {
    const metricas = calcularMetricas(video);
    
    // Normalizar métricas para 0-100
    const scoreEngajamento = Math.min(100, parseFloat(metricas.engajamento) * 20); // 5% = 100 pontos
    const scoreViews = Math.min(100, (metricas.viewsPorDia / 10000) * 100); // 10k/dia = 100 pontos
    const scoreLikes = Math.min(100, parseFloat(metricas.likeRatio) * 10); // 10% = 100 pontos
    
    // Peso: 40% engajamento, 35% views/dia, 25% like ratio
    const scoreFinal = (scoreEngajamento * 0.4) + (scoreViews * 0.35) + (scoreLikes * 0.25);
    
    return Math.round(Math.min(100, scoreFinal));
  };

  const getScoreClassificacao = (score) => {
    if (score >= 61) return { emoji: '🔥', label: t('viralVideos.score_viral'), cor: '#34D399' };
    if (score >= 31) return { emoji: '⚡', label: t('viralVideos.score_good'), cor: '#F59E0B' };
    return { emoji: '😐', label: t('viralVideos.score_regular'), cor: '#EF4444' };
  };

  // VPH contextual color: green >= 500, amber 100-500, red < 100
  const vphColor = (vph) => {
    if (vph >= 500) return '#34D399';
    if (vph >= 100) return '#F59E0B';
    return '#EF4444';
  };

  // 💡 FUNÇÃO PARA GERAR INSIGHTS INTELIGENTES
  const gerarInsights = (video) => {
    const metricas = calcularMetricas(video);
    const insights = [];
    
    // A) ANÁLISE DE CRESCIMENTO (baseado em idade do vídeo)
    const diasDesdePublicacao = metricas.diasDesdePublicacao;
    const viewsPorDia = metricas.viewsPorDia;
    
    if (diasDesdePublicacao <= 7 && viewsPorDia > 100000) {
      insights.push({
        emoji: '🔥',
        titulo: t('viralVideos.insight_viral_recent_title'),
        descricao: t('viralVideos.insight_viral_recent_desc', { days: diasDesdePublicacao, vpd: formatNumber(viewsPorDia) })
      });
    } else if (diasDesdePublicacao <= 30 && viewsPorDia > 50000) {
      insights.push({
        emoji: '⚡',
        titulo: t('viralVideos.insight_strong_growth_title'),
        descricao: t('viralVideos.insight_strong_growth_desc', { days: diasDesdePublicacao, vpd: formatNumber(viewsPorDia) })
      });
    } else if (diasDesdePublicacao > 180 && viewsPorDia > 10000) {
      insights.push({
        emoji: '♻️',
        titulo: t('viralVideos.insight_evergreen_title'),
        descricao: t('viralVideos.insight_evergreen_desc', { days: diasDesdePublicacao, vpd: formatNumber(viewsPorDia) })
      });
    } else if (viewsPorDia < 5000) {
      insights.push({
        emoji: '📉',
        titulo: t('viralVideos.insight_slowing_title'),
        descricao: t('viralVideos.insight_slowing_desc', { vpd: formatNumber(viewsPorDia) })
      });
    }
    
    // B) ANÁLISE DE ENGAJAMENTO
    const engajamento = parseFloat(metricas.engajamento);
    
    if (engajamento > 4) {
      insights.push({
        emoji: '🔥',
        titulo: t('viralVideos.insight_excellent_engagement_title'),
        descricao: t('viralVideos.insight_excellent_engagement_desc', { rate: metricas.engajamento })
      });
    } else if (engajamento > 2) {
      insights.push({
        emoji: '✅',
        titulo: t('viralVideos.insight_high_engagement_title'),
        descricao: t('viralVideos.insight_high_engagement_desc', { rate: metricas.engajamento })
      });
    } else if (engajamento > 1) {
      insights.push({
        emoji: '😐',
        titulo: t('viralVideos.insight_regular_engagement_title'),
        descricao: t('viralVideos.insight_regular_engagement_desc', { rate: metricas.engajamento })
      });
    } else {
      insights.push({
        emoji: '⚠️',
        titulo: t('viralVideos.insight_low_engagement_title'),
        descricao: t('viralVideos.insight_low_engagement_desc', { rate: metricas.engajamento })
      });
    }
    
    // C) ANÁLISE DE QUALIDADE (Like Ratio)
    const likeRatio = parseFloat(metricas.likeRatio);
    
    if (likeRatio > 3) {
      insights.push({
        emoji: '❤️',
        titulo: t('viralVideos.insight_highly_rated_title'),
        descricao: t('viralVideos.insight_highly_rated_desc', { rate: metricas.likeRatio })
      });
    } else if (likeRatio > 1.5) {
      insights.push({
        emoji: '👍',
        titulo: t('viralVideos.insight_quality_approved_title'),
        descricao: t('viralVideos.insight_quality_approved_desc', { rate: metricas.likeRatio })
      });
    } else if (likeRatio > 0.5) {
      insights.push({
        emoji: '😐',
        titulo: t('viralVideos.insight_mixed_reception_title'),
        descricao: t('viralVideos.insight_mixed_reception_desc', { rate: metricas.likeRatio })
      });
    } else {
      insights.push({
        emoji: '👎',
        titulo: t('viralVideos.insight_low_approval_title'),
        descricao: t('viralVideos.insight_low_approval_desc', { rate: metricas.likeRatio })
      });
    }
    
    // D) SEMPRE INCLUIR EXPLICAÇÃO DO SCORE - REMOVIDO
    // Agora explicação está no topo da página
    
    // Limitar a 4 insights (os mais relevantes)
    return insights.slice(0, 4);
  };

  const searchVideos = async () => {
    if (!searchTerm.trim()) {
      toast.error(t('viralVideos.error_search_term'));
      return;
    }

    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      let allVideos = [];
      let pageToken = '';
      const targetResults = 100;
      const resultsPerPage = 50;
      const pagesToFetch = 2;
      
      // 🎯 ESTRATÉGIA: Buscar vídeos usando ROTAÇÃO AUTOMÁTICA de API Keys
      for (let page = 0; page < pagesToFetch; page++) {
        // Build search params
        const searchParams = {
          part: 'snippet',
          type: 'video',
          order: 'viewCount',
          safeSearch: 'none',
          videoEmbeddable: 'true',
          videoSyndicated: 'true',
          maxResults: resultsPerPage,
          q: searchTerm
        };
        
        if (pageToken) searchParams.pageToken = pageToken;
        
        // Aplicar filtro "Publicado há" com prioridade sobre datas manuais
        if (publishedAgoFilter && publishedAgoFilter !== 'all') {
          const selectedOption = publishedAgoOptions.find(opt => opt.value === publishedAgoFilter);
          if (selectedOption && selectedOption.hours) {
            const hoursAgo = new Date(Date.now() - selectedOption.hours * 60 * 60 * 1000);
            searchParams.publishedAfter = hoursAgo.toISOString();
          }
        } else {
          if (publishedAfter) searchParams.publishedAfter = new Date(publishedAfter).toISOString();
          if (publishedBefore) searchParams.publishedBefore = new Date(publishedBefore).toISOString();
        }
        
        // Call backend proxy with automatic key rotation
        const searchResponse = await axios.post(
          `${API}/videos-virais/youtube-search`,
          { params: searchParams },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        const searchData = searchResponse.data;

        if (!searchData.items || searchData.items.length === 0) break;

        // Get video IDs
        const videoIds = searchData.items.map(item => item.id.videoId).join(',');
        
        // Get video statistics and details using rotation
        const statsResponse = await axios.post(
          `${API}/videos-virais/youtube-videos`,
          { 
            params: {
              part: 'statistics,contentDetails,snippet',
              id: videoIds
            }
          },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        const statsData = statsResponse.data;
        
        // Get channel details using rotation
        const channelIds = [...new Set(statsData.items.map(item => item.snippet.channelId))].join(',');
        const channelResponse = await axios.post(
          `${API}/videos-virais/youtube-channels`,
          {
            params: {
              part: 'snippet,statistics',
              id: channelIds
            }
          },
          { headers: { Authorization: `Bearer ${token}` }}
        );
        
        const channelData = channelResponse.data;
        
        // Combine data
        const pageVideos = statsData.items.map(video => {
          const channel = channelData.items.find(ch => ch.id === video.snippet.channelId);
          
          // Safely parse duration (may be missing for very recent videos)
          const duration = video.contentDetails?.duration || 'PT0S';
          const durationInSeconds = parseDurationToSeconds(duration);
          
          return {
            id: video.id,
            title: video.snippet.title,
            channelTitle: video.snippet.channelTitle,
            channelId: video.snippet.channelId,
            thumbnail: video.snippet.thumbnails.medium.url,
            publishedAt: video.snippet.publishedAt,
            views: parseInt(video.statistics.viewCount || 0),
            likes: parseInt(video.statistics.likeCount || 0),
            comments: parseInt(video.statistics.commentCount || 0),
            duration: duration,
            durationInSeconds: durationInSeconds,
            channelCreatedAt: channel ? channel.snippet.publishedAt : null,
            channelSubscribers: channel ? parseInt(channel.statistics.subscriberCount || 0) : 0
          };
        });
        
        allVideos = [...allVideos, ...pageVideos];
        
        // Get next page token
        pageToken = searchData.nextPageToken || '';
        if (!pageToken || page >= 1) break;
      }
      
      // Filter out Shorts and very short videos
      const durationThreshold = parseInt(minDuration) || 300;
      let filtered = allVideos.filter(v => v.durationInSeconds >= durationThreshold);
      
      // Apply additional filters
      if (minViews) filtered = filtered.filter(v => v.views >= parseInt(minViews));
      
      // Sort
      filtered.sort((a, b) => {
        let aVal, bVal;
        
        if (sortBy === 'vph') {
          // Calcular VPH (Views Por Hora)
          const calculateVPH = (video) => {
            const now = new Date();
            const publishedDate = new Date(video.publishedAt);
            const hoursAgo = (now - publishedDate) / (1000 * 60 * 60);
            return hoursAgo > 0 ? video.views / hoursAgo : 0;
          };
          aVal = calculateVPH(a);
          bVal = calculateVPH(b);
        } else {
          aVal = a[sortBy];
          bVal = b[sortBy];
        }
        
        return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
      });
      
      // Limit results
      filtered = filtered.slice(0, targetResults);
      
      setVideos(filtered);
      
      if (filtered.length > 0) {
        const avgViews = Math.round(filtered.reduce((sum, v) => sum + v.views, 0) / filtered.length);
        toast.success(`🎯 ${t('viralVideos.videos_found', { count: filtered.length, avg: avgViews.toLocaleString() })}`);
      } else {
        toast.warning(t('viralVideos.no_videos_filters'));
      }
    } catch (error) {
      console.error('Erro ao buscar vídeos:', error);
      
      // Show specific error message from backend
      const errorMessage = error.response?.data?.detail || error.message;
      const statusCode = error.response?.status;
      
      // Special handling for quota exceeded (429 status)
      if (statusCode === 429 || errorMessage.includes('limite diário')) {
        toast.error(`⚠️ ${t('viralVideos.error_quota_exceeded')}`, { 
          duration: 6000,
          style: {
            background: '#EF4444',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600'
          }
        });
      } else if (errorMessage.includes('API Keys')) {
        toast.error(errorMessage, { duration: 5000 });
      } else {
        toast.error(t('viralVideos.error_search_videos', { message: errorMessage }));
      }
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setPublishedAfter('');
    setPublishedBefore('');
    setMinViews('');
    setPublishedAgoFilter('all');
    setMinDuration('300');
    setVideos([]);
  };

  // 💾 FUNÇÕES DE BUSCAS SALVAS
  const saveSearch = () => {
    if (!searchTerm.trim()) {
      toast.error(t('viralVideos.error_save_search_filters'));
      return;
    }

    if (savedSearches.length >= 15) {
      toast.error(t('viralVideos.error_max_saved_searches'));
      return;
    }

    setShowSaveDialog(true);
  };

  const confirmSaveSearch = () => {
    if (!searchName.trim()) {
      toast.error(t('viralVideos.error_search_name'));
      return;
    }

    const newSearch = {
      id: Date.now().toString(),
      name: searchName.trim(),
      filters: {
        searchTerm,
        publishedAfter,
        publishedBefore,
        minViews,
        publishedAgoFilter,
        minDuration
      },
      createdAt: new Date().toISOString()
    };

    const updatedSearches = [...savedSearches, newSearch];
    setSavedSearches(updatedSearches);
    localStorage.setItem('saved_searches_viral_videos', JSON.stringify(updatedSearches));
    
    toast.success(t('viralVideos.search_saved_success', { name: searchName }));
    setShowSaveDialog(false);
    setSearchName('');
  };

  const loadSavedSearch = (search) => {
    const { filters } = search;
    setSearchTerm(filters.searchTerm);
    setPublishedAfter(filters.publishedAfter || '');
    setPublishedBefore(filters.publishedBefore || '');
    setMinViews(filters.minViews || '');
    setPublishedAgoFilter(filters.publishedAgoFilter || 'all');
    setMinDuration(filters.minDuration || '300');
    
    toast.success(t('viralVideos.search_loaded', { name: search.name }));
  };

  const deleteSavedSearch = (searchId) => {
    const updatedSearches = savedSearches.filter(s => s.id !== searchId);
    setSavedSearches(updatedSearches);
    localStorage.setItem('saved_searches_viral_videos', JSON.stringify(updatedSearches));
    toast.success(t('viralVideos.search_deleted'));
  };

  const formatNumber = (num) => {
    // Handle invalid numbers
    if (num === null || num === undefined || isNaN(num)) {
      return '0';
    }
    
    // Ensure num is a number
    const n = Number(num);
    
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  const formatDuration = (duration) => {
    // Handle null, undefined or empty duration
    if (!duration || typeof duration !== 'string') {
      return '0:00';
    }
    
    // Handle special cases
    if (duration === 'P0D' || duration === 'PT0S') {
      return '0:00';
    }
    
    // Try standard format: PT#H#M#S
    let match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    
    // If no match, try alternative format with days
    if (!match || (!match[1] && !match[2] && !match[3])) {
      return '0:00';
    }
    
    const hours = match[1] ? match[1] : '';
    const minutes = match[2] ? match[2] : '0';
    const seconds = match[3] ? match[3] : '0';
    
    if (hours) {
      return `${hours}:${minutes.padStart(2, '0')}:${seconds.padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.padStart(2, '0')}`;
  };

  const parseDurationToSeconds = (duration) => {
    // Handle null, undefined or empty duration
    if (!duration || typeof duration !== 'string') {
      return 0;
    }
    
    // Handle special cases
    if (duration === 'P0D' || duration === 'PT0S') {
      return 0;
    }
    
    // Try standard format: PT#H#M#S
    let match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    
    // If no match, try alternative format: P#D
    if (!match || (!match[1] && !match[2] && !match[3])) {
      match = duration.match(/P(?:(\d+)D)?T?(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      
      if (match && match[1]) {
        // P#D format (days)
        const days = parseInt(match[1]) || 0;
        return days * 86400; // Convert days to seconds
      }
    }
    
    // If still no valid match, log and return 0
    if (!match || (!match[1] && !match[2] && !match[3])) {
      console.warn('Invalid duration format:', duration);
      return 0;
    }
    
    const hours = parseInt(match[1] || '0') || 0;
    const minutes = parseInt(match[2] || '0') || 0;
    const seconds = parseInt(match[3] || '0') || 0;
    
    const result = hours * 3600 + minutes * 60 + seconds;
    
    // Ensure we return a valid number, not NaN
    return isNaN(result) ? 0 : result;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  // Show blocked message if no access
  if (accessCheck.loading) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
        <Header />
        <main className="dp-main-content" style={{
          marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
          marginTop: '60px',
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'margin-left 0.3s ease',
          fontFamily: 'var(--dp-font)',
          boxSizing: 'border-box',
        }}>
          <div style={{ position: 'relative', width: '64px', height: '64px' }}>
            <div style={{
              width: '64px',
              height: '64px',
              border: '4px solid',
              borderColor: isDarkMode ? '#1F2937' : '#E5E7EB',
              borderTopColor: 'var(--dp-blue-500, #0DAEFF)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div style={{
              position: 'absolute',
              inset: 0,
              width: '64px',
              height: '64px',
              border: '4px solid transparent',
              borderTopColor: '#3B82F6',
              borderRadius: '50%',
              animation: 'spin 1.5s linear infinite reverse'
            }}></div>
          </div>
        </main>
      </div>
    );
  }

  // Sem acesso → vitrine de conversão (mesmo padrão do Niche Finder).
  if (!accessCheck.hasAccess) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
        <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
        <Header />
        <FeatureUpsellPage
          icon={<TrendingUp size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
          title={t('viralVideos.page_title')}
          subtitle={t('viralVideos.page_subtitle')}
          upsell={{
            badge: t('viralVideos.upsell_badge'),
            headline: t('viralVideos.upsell_headline'),
            subhead: t('viralVideos.upsell_subhead'),
            bullets: [
              { Icon: Search, text: t('viralVideos.upsell_bullet_search') },
              { Icon: Gauge, text: t('viralVideos.upsell_bullet_multiplier') },
              { Icon: Bookmark, text: t('viralVideos.upsell_bullet_save') },
              { Icon: Play, text: t('viralVideos.upsell_bullet_replicate') },
            ],
            ctaLabel: t('viralVideos.upsell_cta'),
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <Sidebar onLogout={onLogout} isAdmin={isAdmin} />
      <Header />
      
      {/* Main Content */}
      <main className="dp-main-content" style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        marginTop: '60px',
        flex: 1,
        minWidth: 0,
        transition: 'margin-left 0.3s ease',
        fontFamily: 'var(--dp-font)',
        boxSizing: 'border-box',
        animation: 'dp-pageFadeIn 0.35s ease',
      }}>
        {/* Header — padrão premium compartilhado */}
        <PageHeader
          icon={<TrendingUp size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
          title={t('viralVideos.page_title')}
          description={t('viralVideos.page_subtitle')}
        />

        {/* Dialog para Salvar Busca */}
        <div className="mb-6">
          <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
            <DialogContent className={`max-w-md ${
              isDarkMode ? 'bg-[#252b3b] border-gray-800 text-white' : 'bg-white'
            }`}>
              <DialogHeader>
                <DialogTitle className={isDarkMode ? 'text-white' : 'text-gray-900'}>
                  {t('viralVideos.save_search_title')}
                </DialogTitle>
                <p className={`text-sm mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {t('viralVideos.save_search_desc')}
                </p>
              </DialogHeader>

              <div className="space-y-4 mt-4">
                <div>
                  <label 
                    htmlFor="search_name" 
                    style={{
                      display: 'block',
                      fontSize: '13px',
                      fontWeight: '500',
                      color: 'var(--dp-text-secondary)',
                      marginBottom: '6px'
                    }}
                  >
                    {t('viralVideos.search_name_label')} <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    id="search_name"
                    type="text"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder={t('viralVideos.search_name_placeholder')}
                    maxLength={50}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      background: isDarkMode ? '#0A0A0A' : '#F9FAFB',
                      border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '8px',
                      color: 'var(--dp-text-primary)',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--dp-blue-500)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--dp-border-subtle)'}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        confirmSaveSearch();
                      }
                    }}
                  />
                  <p style={{
                    fontSize: '12px',
                    marginTop: '4px',
                    color: 'var(--dp-text-secondary)'
                  }}>
                    {t('viralVideos.chars_count', { count: searchName.length })}
                  </p>
                </div>

                <div style={{
                  padding: '12px',
                  borderRadius: '8px',
                  background: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)',
                  border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.2)'}`
                }}>
                  <p style={{
                    fontSize: '12px',
                    color: 'var(--dp-text-secondary)',
                    margin: 0
                  }}>
                    📌 <strong style={{ color: 'var(--dp-text-primary)' }}>{t('viralVideos.search_term_label')}</strong> "{searchTerm}"
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setShowSaveDialog(false);
                      setSearchName('');
                    }}
                    style={{
                      flex: 1,
                      padding: '10px 20px',
                      background: 'transparent',
                      color: 'var(--dp-text-primary)',
                      border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--dp-bg-card)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    {t('viralVideos.cancel')}
                  </button>

                  <button
                    className="dp-btn-cta dp-btn-cta--sm"
                    onClick={confirmSaveSearch}
                    disabled={!searchName.trim()}
                    style={{ flex: 1 }}
                  >
                    <Save size={14} />
                    {t('viralVideos.save')}
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* 💾 BUSCAS SALVAS - COMPACTO */}
        {savedSearches.length > 0 && (
          <div className={`rounded-xl p-3 mb-4 ${
            isDarkMode
              ? 'bg-[#111111] border border-gray-800/50'
              : 'bg-white border border-gray-200 shadow-sm'
          }`}>
            <div className="flex items-center gap-2 mb-2">
              <Bookmark className={`w-4 h-4 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              <h2 className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('viralVideos.saved_searches')}
              </h2>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
              }`}>
                {savedSearches.length}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {savedSearches.map((search) => (
                <div
                  key={search.id}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs transition-all ${
                    isDarkMode
                      ? 'bg-gray-900/50 border-gray-800 hover:border-cyan-700'
                      : 'bg-gray-50 border-gray-200 hover:border-cyan-300'
                  }`}
                >
                  <button
                    onClick={() => loadSavedSearch(search)}
                    className={`flex items-center gap-1.5 font-medium ${
                      isDarkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-600 hover:text-cyan-700'
                    }`}
                    title={`${t('viralVideos.search_videos')}: "${search.filters.searchTerm}"`}
                  >
                    <Play size={12} />
                    {search.name}
                  </button>
                  
                  <button
                    onClick={() => deleteSavedSearch(search.id)}
                    className={`p-0.5 rounded hover:bg-red-500/10 transition-colors ${
                      isDarkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'
                    }`}
                    title={t('viralVideos.delete_search')}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search Filters - Design Elegante V2.0 */}
        <div className="dp-top-accent" style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-subtle)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <SlidersHorizontal size={16} color={'var(--dp-text-secondary)'} />
            <h2 style={{ 
              fontSize: '14px', 
              fontWeight: '700', 
              color: 'var(--dp-text-primary)',
              margin: 0
            }}>
              {t('viralVideos.search_filters')}
            </h2>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {/* Search Term + Botão Buscar */}
            <div style={{ gridColumn: '1 / -1' }}>
              {/* Label e Botões (Tutorial + API Key) */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'var(--dp-text-secondary)',
                  textTransform: 'uppercase'
                }}>
                  {t('viralVideos.keywords_label')}
                </label>
                
                {/* Container dos Botões */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  {/* Botão Tutorial */}
                  <button
                    onClick={() => navigate('/tutoriais')}
                    style={{
                      padding: '3px 8px',
                      background: 'rgba(59, 130, 246, 0.15)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '6px',
                      color: '#3B82F6',
                      fontSize: '9px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.8';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    <BookOpen size={9} />
                    {t('viralVideos.tutorial')}
                  </button>
                  {/* Botão de API Key removido (Fase 4 da unificação):
                      as buscas usam o hub global — sem chave obrigatória. */}
                </div>
              </div>
              
              {/* Input + Botão lado a lado */}
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('viralVideos.search_placeholder')}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: 'var(--dp-bg-primary)',
                    color: 'var(--dp-text-primary)',
                    border: '1px solid var(--dp-border-subtle)',
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}
                />
                
              </div>
              
              <div style={{
                marginTop: '8px',
                padding: '10px 12px',
                borderRadius: '8px',
                background: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)',
                border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.2)' : 'rgba(6, 182, 212, 0.15)'}`
              }}>
                <p style={{
                  fontSize: '12px',
                  color: 'var(--dp-text-secondary)',
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ fontSize: '14px' }}>💡</span>
                  <span><strong>{t('viralVideos.tip_label')}</strong> {t('viralVideos.search_tip')}</span>
                </p>
              </div>
            </div>

            {/* Publicado há */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--dp-text-secondary)',
                marginBottom: '6px',
                textTransform: 'uppercase'
              }}>
                {t('viralVideos.published_ago')}
              </label>
              <CustomDropdown
                value={publishedAgoFilter}
                onChange={(val) => setPublishedAgoFilter(val)}
                options={publishedAgoOptions}
                placeholder={t('viralVideos.select_period')}
              />
            </div>

            {/* Duração Mínima */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--dp-text-secondary)',
                marginBottom: '6px',
                textTransform: 'uppercase'
              }}>
                {t('viralVideos.min_duration_label')}
              </label>
              <input
                type="number"
                value={minDuration}
                onChange={(e) => setMinDuration(e.target.value)}
                min="0"
                placeholder="300"
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--dp-bg-primary)',
                  color: 'var(--dp-text-primary)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Published After */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--dp-text-secondary)',
                marginBottom: '6px',
                textTransform: 'uppercase'
              }}>
                {t('viralVideos.published_after')}
              </label>
              <input
                type="date"
                value={publishedAfter}
                onChange={(e) => setPublishedAfter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--dp-bg-primary)',
                  color: 'var(--dp-text-primary)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Published Before */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--dp-text-secondary)',
                marginBottom: '6px',
                textTransform: 'uppercase'
              }}>
                {t('viralVideos.published_before')}
              </label>
              <input
                type="date"
                value={publishedBefore}
                onChange={(e) => setPublishedBefore(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--dp-bg-primary)',
                  color: 'var(--dp-text-primary)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Min Views */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--dp-text-secondary)',
                marginBottom: '6px',
                textTransform: 'uppercase'
              }}>
                {t('viralVideos.min_views_label')}
              </label>
              <input
                type="number"
                value={minViews}
                onChange={(e) => setMinViews(e.target.value)}
                placeholder={t('viralVideos.min_views_placeholder')}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--dp-bg-primary)',
                  color: 'var(--dp-text-primary)',
                  border: '1px solid var(--dp-border-subtle)',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}
              />
            </div>

          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            marginTop: '20px',
            paddingTop: '20px',
            borderTop: '1px solid var(--dp-border-subtle)'
          }}>
            {/* Botão Buscar Vídeos - PRINCIPAL EM DESTAQUE */}
            <button
              className="dp-btn-cta dp-btn-cta--sm"
              onClick={searchVideos}
              disabled={loading}
            >
              <Search size={14} />
              {loading ? t('viralVideos.searching') : t('viralVideos.search_videos')}
              <ArrowRight size={14} />
            </button>

            {/* Botões secundários - menores e neutros */}
            <button
              onClick={clearFilters}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: 'var(--dp-text-secondary)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-text-secondary)';
                e.currentTarget.style.color = 'var(--dp-text-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                e.currentTarget.style.color = 'var(--dp-text-secondary)';
              }}
            >
              <X size={14} />
              {t('viralVideos.clear_filters')}
            </button>

            <button
              onClick={saveSearch}
              disabled={!searchTerm.trim() || savedSearches.length >= 15}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: (!searchTerm.trim() || savedSearches.length >= 15) ? 'var(--dp-border-subtle)' : 'var(--dp-text-secondary)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: (!searchTerm.trim() || savedSearches.length >= 15) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: (!searchTerm.trim() || savedSearches.length >= 15) ? 0.5 : 1,
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (searchTerm.trim() && savedSearches.length < 15) {
                  e.currentTarget.style.borderColor = 'var(--dp-text-secondary)';
                  e.currentTarget.style.color = 'var(--dp-text-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (searchTerm.trim() && savedSearches.length < 15) {
                  e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                  e.currentTarget.style.color = 'var(--dp-text-secondary)';
                }
              }}
            >
              <BookmarkPlus size={14} />
              {t('viralVideos.save_search')}
            </button>
          </div>

          {/* Métrica Info */}
          <div style={{
            marginTop: '10px',
            padding: '6px 10px',
            borderRadius: '6px',
            background: isDarkMode ? 'rgba(6, 182, 212, 0.08)' : 'rgba(6, 182, 212, 0.05)',
            border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.2)' : 'rgba(6, 182, 212, 0.15)'}`,
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <TrendingUp size={14} style={{ color: 'var(--dp-blue-500)', flexShrink: 0 }} />
            <div>
              <p style={{
                fontSize: '11px',
                color: 'var(--dp-text-secondary)',
                margin: 0,
                lineHeight: '1.3'
              }}>
                <strong style={{ color: 'var(--dp-blue-500)' }}>{t('viralVideos.smart_search_label')}</strong> {t('viralVideos.smart_search_desc')}
              </p>
              <p style={{
                fontSize: '9px',
                color: 'var(--dp-text-secondary)',
                margin: '2px 0 0 0',
                lineHeight: '1.2',
                opacity: 0.7
              }}>
                <strong>{t('viralVideos.viral_score_label')}</strong> {t('viralVideos.viral_score_desc')} 🔥
              </p>
            </div>
          </div>
        </div>

        {/* Tabs - Buscar ou Vídeos Salvos */}
        <div style={{
          marginBottom: '24px',
          borderBottom: '2px solid var(--dp-border-subtle)'
        }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setActiveTab('search')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                color: activeTab === 'search' ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                border: 'none',
                borderBottom: `3px solid ${activeTab === 'search' ? 'var(--dp-blue-500)' : 'transparent'}`,
                fontSize: '14px',
                fontWeight: activeTab === 'search' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '-2px'
              }}
            >
              🔍 {t('viralVideos.tab_search')}
            </button>
            <button
              onClick={() => setActiveTab('saved')}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                color: activeTab === 'saved' ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                border: 'none',
                borderBottom: `3px solid ${activeTab === 'saved' ? 'var(--dp-blue-500)' : 'transparent'}`,
                fontSize: '14px',
                fontWeight: activeTab === 'saved' ? '600' : '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '-2px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Bookmark size={14} />
              {t('viralVideos.tab_saved')} {savedVideos.length > 0 && t('viralVideos.saved_count', { count: savedVideos.length })}
            </button>
          </div>
        </div>

        {/* Videos Grid */}
        {activeTab === 'search' && videos.length > 0 && (
          <div className="space-y-4">
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '24px',
              marginBottom: '16px'
            }}>
              <h2 style={{ 
                fontSize: '18px', 
                fontWeight: '700', 
                color: 'var(--dp-text-primary)',
                margin: 0
              }}>
                {videos.length} <span style={{ fontWeight: '400' }}>{t('viralVideos.found_count')}</span>
              </h2>
              
              {/* Sort options */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ 
                  fontSize: '13px', 
                  fontWeight: '600',
                  color: 'var(--dp-text-secondary)' 
                }}>
                  {t('viralVideos.sort_label')}
                </span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className={`w-48 ${
                    isDarkMode
                      ? 'bg-[#252b3b] border-gray-800 text-white'
                      : 'bg-white border-gray-200'
                  }`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="score">{t('viralVideos.sort_score')}</SelectItem>
                    <SelectItem value="views">{t('viralVideos.sort_views')}</SelectItem>
                    <SelectItem value="vph">{t('viralVideos.sort_vph')}</SelectItem>
                    <SelectItem value="likes">{t('viralVideos.sort_likes')}</SelectItem>
                    <SelectItem value="comments">{t('viralVideos.sort_comments')}</SelectItem>
                    <SelectItem value="publishedAt">{t('viralVideos.sort_date')}</SelectItem>
                    <SelectItem value="channelSubscribers">{t('viralVideos.sort_subscribers')}</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                  className={isDarkMode
                    ? 'border-gray-700 text-gray-300 hover:bg-gray-800'
                    : 'border-gray-300'
                  }
                >
                  {sortOrder === 'desc' ? '↓' : '↑'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
              {videos.map((video) => {
                const isInsightHovered = hoveredInsights === video.id;
                return (
                  <div
                    key={video.id}
                    style={{
                      overflow: 'visible',
                      transform: 'translateY(0)',
                      background: 'var(--dp-bg-card)',
                      border: '1px solid var(--dp-border-subtle)',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      position: 'relative',
                      zIndex: isInsightHovered ? 1000 : 1,
                      minHeight: '420px',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.25)';
                      e.currentTarget.style.borderColor = 'var(--dp-border-medium)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                      e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
                    }}
                  >
                  {/* Thumbnail */}
                  <div className="relative aspect-video" style={{ overflow: 'hidden', borderRadius: '8px 8px 0 0' }}>
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded text-[10px]">
                      <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                      {formatDuration(video.duration)}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-2">
                    <h3
                      style={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: 'var(--dp-text-primary)',
                        minHeight: '34px',
                        lineHeight: '1.3',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        marginBottom: '4px',
                        cursor: 'default',
                      }}
                      onMouseEnter={(e) => handleTitleHover(e, video.title)}
                      onMouseLeave={handleTitleLeave}
                    >
                      {video.title}
                    </h3>

                    <div className="flex items-center gap-1.5 mb-1.5" style={{ minHeight: '16px' }}>
                      <p className={`text-[10px] truncate ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {video.channelTitle}
                      </p>
                      {video.channelSubscribers > 0 && (
                        <span className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-full whitespace-nowrap" style={{
                          background: isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)',
                          color: 'var(--dp-blue-500)'
                        }}>
                          <Users size={8} />
                          {formatNumber(video.channelSubscribers)}
                        </span>
                      )}
                    </div>

                    {/* 📊 MÉTRICAS CARDS - LAYOUT MELHORADO */}
                    {(() => {
                      const metricas = calcularMetricas(video);
                      const score = calcularScore(video);
                      const classificacao = getScoreClassificacao(score);
                      const insights = gerarInsights(video);
                      const isHovered = hoveredInsights === video.id;
                      
                      return (
                        <div style={{ marginBottom: '6px', position: 'relative', zIndex: isHovered ? 1000 : 1 }}>
                          {/* Grid 3 colunas - Views, Likes, Comments */}
                          <div className="cq-grid-3" style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                            gap: '3px',
                            marginBottom: '3px'
                          }}>
                            {/* Views */}
                            <div style={{
                              background: isDarkMode ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                Views
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#3B82F6' }}>
                                {formatNumber(video.views)}
                              </div>
                            </div>

                            {/* Likes */}
                            <div style={{
                              background: isDarkMode ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                Likes
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#34D399' }}>
                                {formatNumber(video.likes)}
                              </div>
                            </div>

                            {/* Comments */}
                            <div style={{
                              background: isDarkMode ? 'rgba(168, 85, 247, 0.08)' : 'rgba(168, 85, 247, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                Coment.
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--dp-blue-500, #0DAEFF)' }}>
                                {formatNumber(video.comments)}
                              </div>
                            </div>
                          </div>

                          {/* Grid 2 colunas - Engajamento e Views/dia */}
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '3px',
                            marginBottom: '3px'
                          }}>
                            {/* VPH (Views por hora - calculado de viewsPorDia) */}
                            <div style={{
                              background: isDarkMode ? 'rgba(6, 182, 212, 0.08)' : 'rgba(6, 182, 212, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                VPH
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: vphColor(Math.round(metricas.viewsPorDia / 24)) }}>
                                {formatNumber(Math.round(metricas.viewsPorDia / 24))}
                              </div>
                            </div>

                            {/* Engajamento */}
                            <div style={{
                              background: isDarkMode ? 'rgba(251, 191, 36, 0.08)' : 'rgba(251, 191, 36, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(251, 191, 36, 0.15)' : 'rgba(251, 191, 36, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                Engaj.
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#FBBF24' }}>
                                {metricas.engajamento}%
                              </div>
                            </div>
                          </div>

                          {/* Grid 2 colunas - Score Viral e Data */}
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '3px',
                            marginBottom: '3px'
                          }}>
                            {/* Score Viral - Card Destaque */}
                            <div 
                              style={{
                                background: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.08)',
                                border: `1px solid ${isDarkMode ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.15)'}`,
                                borderRadius: '5px',
                                padding: '5px',
                                transition: 'all 0.2s ease',
                                position: 'relative'
                              }}
                              onMouseEnter={() => setHoveredInsights(video.id)}
                              onMouseLeave={() => setTimeout(() => setHoveredInsights(null), 200)}
                            >
                              {/* Linha 1: Label e Info Icon */}
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                marginBottom: '4px'
                              }}>
                                <span style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', fontWeight: '500' }}>
                                  {t('viralVideos.viral_score_card')}
                                </span>
                                <span style={{ fontSize: '11px', opacity: 0.5 }}>ℹ️</span>
                              </div>
                              
                              {/* Linha 2: Emoji + Score (Centralizado) */}
                              <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px'
                              }}>
                                <span style={{ fontSize: '18px' }}>{classificacao.emoji}</span>
                                <span style={{ 
                                  fontSize: '18px', 
                                  fontWeight: '800', 
                                  color: classificacao.cor
                                }}>
                                  {score}
                                </span>
                              </div>
                            </div>
                            
                            {/* Data de Publicação - Card Separado */}
                            <div 
                              style={{
                                background: isDarkMode ? 'rgba(6, 182, 212, 0.08)' : 'rgba(6, 182, 212, 0.05)',
                                border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.2)' : 'rgba(6, 182, 212, 0.15)'}`,
                                borderRadius: '5px',
                                padding: '5px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '3px'
                              }}
                            >
                              <span style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', fontWeight: '500' }}>
                                Data de Publicação
                              </span>
                              <div style={{ 
                                fontSize: '11px', 
                                fontWeight: '600',
                                color: isDarkMode ? '#67E8F9' : '#0891B2',
                                textAlign: 'center'
                              }}>
                                📅 {formatDate(video.publishedAt)}
                              </div>
                            </div>
                          </div>
                          
                          {/* 💡 QUADRO DE INSIGHTS INTELIGENTE */}
                          {isHovered && (
                            <div 
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: '0',
                                right: '0',
                                marginTop: '8px',
                                padding: '16px',
                                background: isDarkMode 
                                  ? 'linear-gradient(135deg, rgba(17, 24, 39, 0.98), rgba(31, 41, 55, 0.98))' 
                                  : 'linear-gradient(135deg, rgba(240, 249, 255, 0.98), rgba(224, 242, 254, 0.98))',
                                backdropFilter: 'blur(12px)',
                                border: `2px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.5)' : '#0EA5E9'}`,
                                borderRadius: '12px',
                                boxShadow: isDarkMode 
                                  ? '0 12px 32px rgba(0,0,0,0.8), 0 0 0 1px rgba(6, 182, 212, 0.3)' 
                                  : '0 12px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(6, 182, 212, 0.3)',
                                zIndex: 9999,
                                animation: 'slideDown 0.2s ease-out',
                                pointerEvents: 'auto'
                              }}
                              onMouseEnter={() => setHoveredInsights(video.id)}
                              onMouseLeave={() => setTimeout(() => setHoveredInsights(null), 100)}
                            >
                              {/* Título */}
                              <div style={{
                                fontSize: '14px',
                                fontWeight: '700',
                                color: 'var(--dp-text-primary)',
                                marginBottom: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                borderBottom: '1px solid var(--dp-border-subtle)',
                                paddingBottom: '8px'
                              }}>
                                💡 INSIGHTS
                              </div>
                              
                              {/* Cards de Insights */}
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {insights.map((insight, idx) => (
                                  <div key={idx} style={{
                                    padding: '12px',
                                    background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#FFFFFF',
                                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.08)'}`,
                                    borderRadius: '8px',
                                    fontSize: '11px',
                                    lineHeight: '1.5',
                                    transition: 'transform 0.2s ease',
                                    cursor: 'default'
                                  }}>
                                    <div style={{
                                      fontWeight: '700',
                                      color: 'var(--dp-text-primary)',
                                      marginBottom: '6px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      fontSize: '12px'
                                    }}>
                                      <span>{insight.emoji}</span>
                                      <span>{insight.titulo}</span>
                                    </div>
                                    <div style={{
                                      color: 'var(--dp-text-secondary)',
                                      fontSize: '10.5px',
                                      lineHeight: '1.6'
                                    }}>
                                      {insight.descricao}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Botões de Ação - Compactos */}
                    <div style={{ display: 'flex', gap: '4px', padding: '6px' }}>
                      {/* Botão Salvar Vídeo */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveVideo(video);
                        }}
                        disabled={savingVideo === video.id}
                        style={{
                          padding: '6px 8px',
                          background: savingVideo === video.id
                            ? 'var(--dp-border-subtle)'
                            : 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                          border: 'none',
                          borderRadius: '5px',
                          color: '#FFFFFF',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: savingVideo === video.id ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          transition: 'all 0.2s',
                          boxShadow: savingVideo === video.id ? 'none' : '0 2px 4px rgba(16, 185, 129, 0.2)',
                          opacity: savingVideo === video.id ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (savingVideo !== video.id) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 8px rgba(16, 185, 129, 0.3)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(16, 185, 129, 0.2)';
                        }}
                      >
                        <Bookmark size={11} fill={savingVideo === video.id ? 'none' : '#FFFFFF'} />
                        {savingVideo === video.id ? '...' : t('viralVideos.save_video')}
                      </button>

                      {/* Botão Principal - Ver Vídeo */}
                      <button
                        onClick={() => window.open(`https://www.youtube.com/watch?v=${video.id}`, '_blank')}
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                          border: 'none',
                          borderRadius: '5px',
                          color: '#FFFFFF',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                        }}
                      >
                        <Play size={11} fill="#FFFFFF" />
                        {t('viralVideos.watch_video')}
                      </button>

                      {/* Botão Secundário - Ver Canal */}
                      <button
                        onClick={() => window.open(`https://www.youtube.com/channel/${video.channelId}`, '_blank')}
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          background: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#FFFFFF',
                          border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#E5E7EB'}`,
                          borderRadius: '5px',
                          color: 'var(--dp-text-primary)',
                          fontSize: '11px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#F9FAFB';
                          e.currentTarget.style.borderColor = 'var(--dp-blue-500)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#FFFFFF';
                          e.currentTarget.style.borderColor = isDarkMode ? 'rgba(255, 255, 255, 0.12)' : '#E5E7EB';
                        }}
                      >
                        <Users size={11} />
                        {t('viralVideos.view_channel')}
                      </button>
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          </div>
        )}

        {/* Saved Videos Tab */}
        {activeTab === 'saved' && (
          <div className="space-y-4">
            {savedVideosLoading ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '16px'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
                <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)' }}>
                  {t('viralVideos.loading_saved')}
                </p>
              </div>
            ) : savedVideos.length > 0 ? (
              <>
                <h2 style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: 'var(--dp-text-primary)',
                  marginBottom: '16px'
                }}>
                  {savedVideos.length} <span style={{ fontWeight: '400' }}>{t('viralVideos.saved_videos_count_text')}</span>
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
                  {savedVideos.map((savedVideo) => {
                    const video = savedVideo.video_data;
                    return (
                      <div 
                        key={savedVideo.id}
                        className={`rounded-lg transition-all duration-300 ${
                          isDarkMode
                            ? 'bg-[#252b3b] border border-gray-800/50 hover:border-cyan-500/50'
                            : 'bg-white border border-gray-200 hover:border-cyan-400 shadow-sm'
                        }`}
                        style={{ 
                          boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                      >
                        {/* Thumbnail */}
                        <div className="relative aspect-video" style={{ overflow: 'hidden', borderRadius: '8px 8px 0 0' }}>
                          <img 
                            src={video.thumbnail} 
                            alt={video.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-1.5 right-1.5 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded text-[10px]">
                            <Clock className="w-2.5 h-2.5 inline mr-0.5" />
                            {formatDuration(video.duration)}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-2">
                          <h3 className={`font-semibold text-sm line-clamp-2 mb-1 ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`} style={{ 
                            minHeight: '34px',
                            lineHeight: '1.3'
                          }}>
                            {video.title}
                          </h3>

                          <div className="flex items-center gap-1.5 mb-1.5" style={{ minHeight: '16px' }}>
                            <p className={`text-[10px] truncate ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {video.channelTitle}
                            </p>
                          </div>

                          {/* Termo de Busca Badge */}
                          <div style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            background: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)',
                            border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.2)' : 'rgba(6, 182, 212, 0.15)'}`,
                            marginBottom: '8px'
                          }}>
                            <p style={{
                              fontSize: '9px',
                              color: 'var(--dp-blue-500)',
                              margin: 0,
                              fontWeight: '500'
                            }}>
                              🔍 Termo: {savedVideo.termo_busca}
                            </p>
                          </div>

                          {/* Metrics */}
                          <div className="cq-grid-3" style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                            gap: '3px',
                            marginBottom: '6px'
                          }}>
                            <div style={{
                              background: isDarkMode ? 'rgba(59, 130, 246, 0.08)' : 'rgba(59, 130, 246, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                Views
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#3B82F6' }}>
                                {formatNumber(video.views)}
                              </div>
                            </div>

                            <div style={{
                              background: isDarkMode ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                Likes
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: '#34D399' }}>
                                {formatNumber(video.likes)}
                              </div>
                            </div>

                            <div style={{
                              background: isDarkMode ? 'rgba(168, 85, 247, 0.08)' : 'rgba(168, 85, 247, 0.05)',
                              border: `1px solid ${isDarkMode ? 'rgba(168, 85, 247, 0.15)' : 'rgba(168, 85, 247, 0.1)'}`,
                              borderRadius: '5px',
                              padding: '4px',
                              textAlign: 'center'
                            }}>
                              <div style={{ fontSize: '9px', color: 'var(--dp-text-secondary)', marginBottom: '2px', fontWeight: '500' }}>
                                Coment.
                              </div>
                              <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--dp-blue-500, #0DAEFF)' }}>
                                {formatNumber(video.comments)}
                              </div>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div style={{ display: 'flex', gap: '4px', padding: '6px' }}>
                            <button
                              onClick={() => window.open(`https://www.youtube.com/watch?v=${savedVideo.video_id}`, '_blank')}
                              style={{
                                flex: 1,
                                padding: '6px 8px',
                                background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
                                border: 'none',
                                borderRadius: '5px',
                                color: '#FFFFFF',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
                              }}
                            >
                              <Play size={11} fill="#FFFFFF" />
                              {t('viralVideos.watch_video')}
                            </button>

                            <button
                              onClick={() => handleDeleteSavedVideo(savedVideo.video_id)}
                              style={{
                                padding: '6px 8px',
                                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                                border: 'none',
                                borderRadius: '5px',
                                color: '#FFFFFF',
                                fontSize: '11px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                transition: 'all 0.2s',
                                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
                              }}
                            >
                              <Trash2 size={11} />
                              {t('viralVideos.remove_video')}
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'var(--dp-bg-card)',
                border: '1px solid var(--dp-border-subtle)',
                borderRadius: '16px'
              }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>📚</div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--dp-text-primary)', marginBottom: '8px' }}>
                  {t('viralVideos.no_saved_videos')}
                </h3>
                <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', marginBottom: '20px' }}>
                  {t('viralVideos.no_saved_videos_desc_line1')}<br />
                  {t('viralVideos.no_saved_videos_desc_line2')}
                </p>
                <button
                  className="dp-btn-cta dp-btn-cta--sm"
                  onClick={() => setActiveTab('search')}
                >
                  <Search size={14} /> {t('viralVideos.go_search')} <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State - Design V2.0 */}
        {activeTab === 'search' && !loading && videos.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '60px',
            background: 'var(--dp-bg-card)',
            border: '1px solid var(--dp-border-subtle)',
            borderRadius: '16px'
          }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎯</div>
            <h3 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--dp-text-primary)', marginBottom: '8px' }}>
              {t('viralVideos.empty_state_title')}
            </h3>
            <p style={{ fontSize: '14px', color: 'var(--dp-text-secondary)', marginBottom: '20px' }}>
              {t('viralVideos.empty_state_desc_line1')}<br />
              {t('viralVideos.empty_state_desc_line2')}
            </p>
            <div style={{
              display: 'inline-flex',
              gap: '16px',
              padding: '20px',
              background: isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)',
              borderRadius: '12px',
              marginTop: '16px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🔍</div>
                <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)' }} dangerouslySetInnerHTML={{ __html: t('viralVideos.empty_search_niche').replace('\n', '<br/>') }} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>📊</div>
                <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)' }} dangerouslySetInnerHTML={{ __html: t('viralVideos.empty_analyze').replace('\n', '<br/>') }} />
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>🎯</div>
                <div style={{ fontSize: '11px', color: 'var(--dp-text-secondary)' }} dangerouslySetInnerHTML={{ __html: t('viralVideos.empty_find').replace('\n', '<br/>') }} />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Tooltip for truncated titles */}
      {showTooltip && tooltipText && (
        <div style={{
          position: 'fixed',
          left: tooltipPos.x,
          top: tooltipPos.y,
          transform: 'translate(-50%, -100%)',
          background: 'var(--dp-bg-elevated)',
          color: 'var(--dp-text-primary)',
          border: '1px solid var(--dp-border-medium)',
          padding: '6px 12px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: 500,
          maxWidth: '320px',
          zIndex: 9999,
          pointerEvents: 'none',
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
          lineHeight: 1.4,
        }}>
          {tooltipText}
        </div>
      )}
    </div>
  );
};

export default VideosVirais;
